import { motion, useReducedMotion } from 'framer-motion'

/* ── Mathematics in the background ───────────────────────────────────────
   Faint equations and transformation matrices floating behind the hero at
   three depths: far ones small and softened, near ones larger and crisp. Each
   drifts slowly on its own clock, and the depth layers pull them apart as the
   page scrolls and the pointer moves. Placed only in open space — around the
   capability sheet and under the copy — never behind text. Decorative only. */

// A bracketed matrix, e.g. a 2D translation in homogeneous coordinates.
function Matrix({ rows }) {
  return (
    <span className="math-matrix">
      {rows.map((r, i) => (
        <span key={i} className="math-row">
          {r.map((c, j) => (
            <span key={j}>{c}</span>
          ))}
        </span>
      ))}
    </span>
  )
}

const TRANSLATE = (
  <>
    T = <Matrix rows={[['1', '0', 'tₓ'], ['0', '1', 'tᵧ'], ['0', '0', '1']]} />
  </>
)
const ROTATE = (
  <>
    R(θ) = <Matrix rows={[['cos θ', '−sin θ'], ['sin θ', 'cos θ']]} />
  </>
)

// [content, position classes, depth (0 near … 2 far), drift seconds]
// Few, and well spaced: one identity above the sheet, two transforms under
// it. Restraint is what keeps the scene reading as precise, not busy.
const ITEMS = [
  ['e^(iπ) + 1 = 0', 'right-[9%] top-[8.5rem] hidden lg:block', 2, 14],
  [TRANSLATE, 'right-[31%] bottom-[2.75rem] hidden xl:block', 1, 12],
  [ROTATE, 'right-[4%] bottom-[3.25rem] hidden lg:block', 0, 13],
  ['p′ = M · p', 'right-[6%] bottom-[3rem] lg:hidden', 1, 10],
]

// Nearer layers move more with scroll/pointer and read clearer.
const DEPTH = [
  { depth: 0.35, cls: 'text-[15px] md:text-base opacity-[0.32]' },
  { depth: 0.9, cls: 'text-[13px] md:text-[14px] opacity-[0.24]' },
  { depth: 1.5, cls: 'text-[12px] md:text-[13px] opacity-[0.18] blur-[0.5px]' },
]

export default function MathField() {
  const reduce = useReducedMotion()
  return DEPTH.map((d, level) => (
    <div key={level} className="depth-layer" style={{ '--depth': d.depth }} aria-hidden="true">
      {ITEMS.filter((it) => it[2] === level).map(([content, pos, , secs], i) => (
        // Faintness lives on the outer box; the entrance fade animates the
        // inner one, so it can't override it.
        <div key={i} className={`math-item absolute ${pos} ${d.cls}`}>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.2, 0, 0, 1], delay: 0.8 + level * 0.2 + i * 0.1 }}
          >
            <span className="math-float" style={{ animationDuration: `${secs}s`, animationDelay: `${-i * 1.7}s` }}>
              {content}
            </span>
          </motion.div>
        </div>
      ))}
    </div>
  ))
}
